import { useState, useRef, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FileUpload } from '../components/FileUpload';
import { DetectAbnormalLayoutRequestForm, DetectAbnormalLayoutRequestFormRef, FormDataWithAuth } from '../components/DetectAbnormalLayoutRequestForm';
import { DetectAbnormalLayoutResults } from '../components/DetectAbnormalLayoutResults';
import { MerchantSampleSelector, PREDEFINED_MERCHANTS, ReferenceMode } from '../components/MerchantSampleSelector';
import { detectAbnormalLayout, getMerchantSamples, ping, ApiError } from '../api';
import { DetectAbnormalLayoutRequest, DetectAbnormalLayoutResponse } from '../types';
import { config } from '../config';

enum ProcessingStep {
  FILE_SELECTION = 'file_selection',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error'
}

interface DocumentState {
  id: string;
  file: File;
  previewUrl: string;
  step: ProcessingStep;
  results: DetectAbnormalLayoutResponse | null;
  error: string;
  referenceImages: File[];
  merchantName?: string;
  previewSampleUrls: string[];
  isLoadingPreview: boolean;
}

const CACHE_KEY = 'formx-detect-abnormal-layout-form-data';

export function DetectAbnormalLayout() {
  // Load cached form data
  const loadCachedData = (): FormDataWithAuth => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsedData = JSON.parse(cached);
        return {
          ...config.defaultDetectAbnormalLayoutFormValues,
          ...parsedData,
          auth_token: parsedData.auth_token || ''
        };
      }
    } catch (error) {
      console.warn('Failed to load cached form data:', error);
    }
    
    return {
      auth_token: '',
      llm_model_provider: config.defaultDetectAbnormalLayoutFormValues.llmModelProvider,
      llm_model_name: config.defaultDetectAbnormalLayoutFormValues.llmModelName,
      chat_complete_request_args: null,
      chat_complete_request_args_enabled: false,
      merchant_name: config.defaultDetectAbnormalLayoutFormValues.merchantName,
      custom_instructions: null,
      custom_instructions_enabled: true,
    };
  };

  const [documents, setDocuments] = useState<DocumentState[]>([]);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isCheckingConnection, setIsCheckingConnection] = useState<boolean>(false);
  const [isConfigCollapsed, setIsConfigCollapsed] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormDataWithAuth>(loadCachedData);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [referenceMode, setReferenceMode] = useState<ReferenceMode>('predefined');
  const formRef = useRef<DetectAbnormalLayoutRequestFormRef>(null);
  const documentsRef = useRef<DocumentState[]>([]);

  // Keep ref in sync with documents state
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  // Get active document
  const activeDocument = documents.find(doc => doc.id === activeDocumentId) || null;

  // Get active document's reference images and merchant name (memoized to prevent unnecessary re-renders)
  const activeReferenceImages = useMemo(() => activeDocument?.referenceImages || [], [activeDocument?.referenceImages]);
  const activeMerchantName = useMemo(() => activeDocument?.merchantName || '', [activeDocument?.merchantName]);

  // Combine global form data with active document's merchant name
  const activeFormData: FormDataWithAuth = useMemo(() => ({
    ...formData,
    merchant_name: activeMerchantName,
  }), [formData, activeMerchantName]);

  // Validation utility function
  const validateFormData = (merchantName: string, referenceImages: File[]): boolean => {
    // Valid if either merchant name is provided OR reference images are uploaded
    const hasMerchantName = !!merchantName && merchantName.trim() !== '';
    const hasReferenceImages = referenceImages.length > 0;
    return hasMerchantName || hasReferenceImages;
  };

  // Update form validity when active document or merchant name changes
  useEffect(() => {
    setIsFormValid(validateFormData(activeMerchantName, activeReferenceImages));
  }, [activeMerchantName, activeReferenceImages]);

  // Cache form data whenever it changes (global LLM config and auth)
  const handleFormDataChange = (newFormData: FormDataWithAuth) => {
    // Update global form data (LLM config and auth)
    setFormData(newFormData);
    
    // Update active document's merchant name
    if (activeDocumentId) {
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, merchantName: newFormData.merchant_name }
          : doc
      ));
    }
    
    // Cache global settings
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(newFormData));
    } catch (error) {
      console.warn('Failed to cache form data:', error);
    }
  };

  const handleValidationChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  const handleCheckConnection = async () => {
    setIsCheckingConnection(true);
    try {
      await ping();
      setIsConnected(true);
      toast.success('Successfully connected to backend');
    } catch (err) {
      setIsConnected(false);
      const errorMessage = err instanceof ApiError ? err.message : 'Failed to connect to backend';
      toast.error(errorMessage);
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const detectMerchantFromFilename = (filename: string): string => {
    // Convert filename to lowercase for case-insensitive matching
    const lowerFilename = filename.toLowerCase();
    
    // Check if any predefined merchant name is a substring of the filename
    for (const merchant of PREDEFINED_MERCHANTS) {
      if (lowerFilename.includes(merchant)) {
        return merchant;
      }
    }
    
    // No match found
    return '';
  };

  const handleFileSelect = async (file: File) => {
    // Create new document
    const documentId = `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const previewUrl = URL.createObjectURL(file);
    
    // Auto-detect merchant name from filename
    const detectedMerchant = detectMerchantFromFilename(file.name);
    
    const newDocument: DocumentState = {
      id: documentId,
      file,
      previewUrl,
      step: ProcessingStep.FILE_SELECTION,
      results: null,
      error: '',
      referenceImages: [],
      merchantName: detectedMerchant,
      previewSampleUrls: [],
      isLoadingPreview: false,
    };
    
    setDocuments(prev => [...prev, newDocument]);
    setActiveDocumentId(documentId);
    
    if (detectedMerchant) {
      toast.success(`File "${file.name}" added successfully. Detected merchant: ${detectedMerchant}`);
    } else {
      toast.success(`File "${file.name}" added successfully`);
    }
  };

  const handleReferenceImageSelect = (file: File) => {
    if (!activeDocumentId) {
      toast.error('Please select an input image first');
      return;
    }
    
    setDocuments(prev => prev.map(doc => 
      doc.id === activeDocumentId 
        ? { ...doc, referenceImages: [...doc.referenceImages, file] }
        : doc
    ));
    
    toast.success(`Reference image "${file.name}" added`);
  };

  const handleRemoveReferenceImage = (index: number) => {
    if (!activeDocumentId) return;
    
    setDocuments(prev => prev.map(doc => 
      doc.id === activeDocumentId 
        ? { ...doc, referenceImages: doc.referenceImages.filter((_, i) => i !== index) }
        : doc
    ));
    
    toast.info('Reference image removed');
  };
  
  const handleOpenImage = () => {
    if (activeDocument?.previewUrl) {
      window.open(activeDocument.previewUrl, '_blank');
    }
  };

  const handleOpenReferenceImage = (file: File) => {
    const url = URL.createObjectURL(file);
    window.open(url, '_blank');
    // Clean up the URL after a short delay
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handlePreviewSamples = async () => {
    if (!activeDocument || !activeDocument.merchantName) {
      toast.error('Please select a merchant first');
      return;
    }

    // Set loading state
    setDocuments(prev => prev.map(doc => 
      doc.id === activeDocumentId 
        ? { ...doc, isLoadingPreview: true, previewSampleUrls: [] }
        : doc
    ));

    try {
      const response = await getMerchantSamples(activeDocument.merchantName, formData.auth_token);
      
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, isLoadingPreview: false, previewSampleUrls: response.sample_urls }
          : doc
      ));

      if (response.sample_urls.length === 0) {
        toast.info('No reference samples found for this merchant');
      } else {
        toast.success(`Loaded ${response.sample_urls.length} reference sample(s)`);
      }
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message}`
        : 'Failed to load reference samples';
      
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, isLoadingPreview: false, previewSampleUrls: [] }
          : doc
      ));
      
      toast.error(errorMessage);
    }
  };

  const handleFormSubmit = async (formData: DetectAbnormalLayoutRequest, formAuthToken: string) => {
    if (!activeDocument) {
      const errorMessage = 'No document selected';
      toast.error(errorMessage);
      return;
    }

    // Update document state to processing
    setDocuments(prev => prev.map(doc => 
      doc.id === activeDocumentId 
        ? { ...doc, step: ProcessingStep.PROCESSING, error: '', results: null }
        : doc
    ));

    try {
      const referenceImages = referenceMode === 'upload' ? activeDocument.referenceImages : null;
      const response = await detectAbnormalLayout(
        activeDocument.file, 
        formData, 
        referenceImages,
        formAuthToken
      );
      
      // Update document state to completed with results
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, step: ProcessingStep.COMPLETED, results: response }
          : doc
      ));
      
      toast.success('Abnormal layout detection completed');
    } catch (err) {
      const errorMessage = err instanceof ApiError 
        ? `${err.message}`
        : 'Processing failed';
      
      // Update document state to error
      setDocuments(prev => prev.map(doc => 
        doc.id === activeDocumentId 
          ? { ...doc, step: ProcessingStep.ERROR, error: errorMessage }
          : doc
      ));
      
      toast.error(errorMessage);
    }
  };

  const handleRemoveDocument = (documentId: string) => {
    const docToRemove = documents.find(doc => doc.id === documentId);
    if (docToRemove) {
      URL.revokeObjectURL(docToRemove.previewUrl);
    }
    
    setDocuments(prev => prev.filter(doc => doc.id !== documentId));
    
    // If removing active document, select another one
    if (activeDocumentId === documentId) {
      const remainingDocs = documents.filter(doc => doc.id !== documentId);
      setActiveDocumentId(remainingDocs.length > 0 ? remainingDocs[0].id : null);
    }
    
    toast.info('Document removed');
  };

  const handleSwitchDocument = (documentId: string) => {
    setActiveDocumentId(documentId);
  };

  const handleDownload = (url: string) => {
    window.open(url, '_blank');
  };

  const isProcessing = activeDocument?.step === ProcessingStep.PROCESSING;

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      // Use ref to access latest documents at cleanup time
      documentsRef.current.forEach(doc => {
        URL.revokeObjectURL(doc.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run cleanup on unmount

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1>FormX Abnormal Layout Detection</h1>
          <nav className="app-nav" aria-label="Main">
            <button 
              className="btn btn-nav"
              onClick={() => window.open('/detect-receipt-fraud', '_blank')}
            >
              Receipt Fraud Detection
            </button>
            <button 
              className="btn btn-nav active"
              onClick={() => window.open('/detect-abnormal-layout', '_blank')}
              aria-current="page"
            >
              Abnormal Layout Detection
            </button>
          </nav>
        </div>
        <div className="connection-status">
          {isConnected === null && (
            <button 
              onClick={handleCheckConnection} 
              className="btn btn-secondary"
              disabled={isCheckingConnection}
            >
              {isCheckingConnection ? 'Checking...' : 'Check Connection'}
            </button>
          )}
          {isConnected === true && (
            <>
              <span className="status-connected">✓ Connected</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Recheck'}
              </button>
            </>
          )}
          {isConnected === false && (
            <>
              <span className="status-disconnected">✗ Backend unavailable</span>
              <button 
                onClick={handleCheckConnection} 
                className="btn btn-secondary"
                disabled={isCheckingConnection}
              >
                {isCheckingConnection ? 'Checking...' : 'Retry Connection'}
              </button>
            </>
          )}
        </div>
      </header>

      <main className="app-main">
        <div className="app-layout">
          <div className="left-panel">
            {/* Input Image Section */}
            <div className="upload-section">
              <h2>Upload Input Image</h2>
              <FileUpload onFileSelect={handleFileSelect} disabled={isProcessing} multiple={true} />
              
              {/* Document List */}
              {documents.length > 0 && (
                <div className="document-list">
                  <h3>Uploaded Documents ({documents.length})</h3>
                  <div className="document-items">
                    {documents.map(doc => (
                      <div 
                        key={doc.id}
                        className={`document-item ${doc.id === activeDocumentId ? 'active' : ''} ${doc.step === ProcessingStep.PROCESSING ? 'processing' : ''}`}
                        onClick={() => handleSwitchDocument(doc.id)}
                      >
                        <div className="document-item-content">
                          <div className="document-icon">
                            {doc.step === ProcessingStep.PROCESSING && '⏳'}
                            {doc.step === ProcessingStep.COMPLETED && '✓'}
                            {doc.step === ProcessingStep.ERROR && '✗'}
                            {doc.step === ProcessingStep.FILE_SELECTION && '📄'}
                          </div>
                          <div className="document-info">
                            <div className="document-name" title={doc.file.name}>
                              {doc.file.name}
                            </div>
                            <div className="document-meta">
                              {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                              {doc.step === ProcessingStep.PROCESSING && ' • Processing...'}
                              {doc.step === ProcessingStep.COMPLETED && ' • Completed'}
                              {doc.step === ProcessingStep.ERROR && ' • Error'}
                            </div>
                            <div className="document-context-info">
                              {doc.merchantName && <span className="context-badge">🏪 {doc.merchantName}</span>}
                              {doc.referenceImages.length > 0 && <span className="context-badge">🖼️ {doc.referenceImages.length} ref</span>}
                            </div>
                          </div>
                        </div>
                        <button
                          className="btn btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDocument(doc.id);
                          }}
                          title="Remove document"
                          disabled={doc.step === ProcessingStep.PROCESSING}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Active Document Actions */}
              {activeDocument && (
                <div className="active-document-actions">
                  <button 
                    onClick={handleOpenImage} 
                    className="btn btn-secondary"
                    title="Open image in new tab"
                  >
                    🔍 Open Image
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    disabled={!isFormValid || isProcessing}
                    onClick={() => {
                      const { auth_token, chat_complete_request_args_enabled, custom_instructions_enabled, ...requestData } = activeFormData;
                      const payload = {
                        ...requestData,
                        chat_complete_request_args: chat_complete_request_args_enabled 
                          ? activeFormData.chat_complete_request_args 
                          : null,
                        custom_instructions: custom_instructions_enabled 
                          ? activeFormData.custom_instructions 
                          : null,
                      };
                      handleFormSubmit(payload, auth_token);
                    }}
                  >
                    {isProcessing ? 'Processing...' : 'Detect Abnormal Layout'}
                  </button>
                </div>
              )}
            </div>

            {/* Document-Specific Settings Section */}
            <div className="upload-section">
              <div className="section-header-with-context">
                <h2>Document Settings</h2>
                {activeDocument && (
                  <span className="context-indicator">
                    For: <strong>{activeDocument.file.name}</strong>
                  </span>
                )}
              </div>
              <p className="section-description">
                Configure merchant and reference samples for the selected input image.
                {!activeDocument && ' Select an input image first.'}
              </p>

              {!activeDocument && (
                <div className="no-document-message">
                  <p>Please select an input image to configure its merchant and reference samples.</p>
                </div>
              )}

              {activeDocument && (
                <MerchantSampleSelector
                  mode={referenceMode}
                  onModeChange={setReferenceMode}
                  merchantName={activeMerchantName}
                  onMerchantNameChange={(name) => {
                    if (!activeDocumentId) return;
                    setDocuments(prev => prev.map(doc => 
                      doc.id === activeDocumentId 
                        ? { ...doc, merchantName: name, previewSampleUrls: [] }
                        : doc
                    ));
                  }}
                  referenceImages={activeReferenceImages}
                  onReferenceImageAdd={handleReferenceImageSelect}
                  onReferenceImageRemove={handleRemoveReferenceImage}
                  onOpenReferenceImage={handleOpenReferenceImage}
                  onPreviewSamples={handlePreviewSamples}
                  previewSampleUrls={activeDocument.previewSampleUrls}
                  isLoadingPreview={activeDocument.isLoadingPreview}
                  disabled={isProcessing}
                />
              )}
            </div>

            <div className="config-section">
              <div 
                className="config-header" 
                onClick={() => setIsConfigCollapsed(!isConfigCollapsed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setIsConfigCollapsed(!isConfigCollapsed);
                  }
                }}
                aria-expanded={!isConfigCollapsed}
                aria-controls="config-form"
              >
                <button 
                  type="button" 
                  className={`collapse-toggle ${isConfigCollapsed ? 'collapsed' : ''}`}
                  aria-label={isConfigCollapsed ? 'Expand configuration' : 'Collapse configuration'}
                >
                  <span>▼</span>
                </button>
                <h2>Configure Detection</h2>
              </div>
              {!isConfigCollapsed && (
                <div id="config-form">
                  <DetectAbnormalLayoutRequestForm 
                    ref={formRef}
                    formData={activeFormData}
                    onFormDataChange={handleFormDataChange}
                    onSubmit={(requestData, authToken) => handleFormSubmit(requestData, authToken)}
                    disabled={isProcessing}
                    onValidationChange={handleValidationChange}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Middle Panel - Image Preview */}
          <div className="middle-panel">
            {activeDocument && (
              <div className="image-preview-panel">
                <div className="image-preview-header">
                  <h3>Input Image Preview</h3>
                  <button 
                    onClick={handleOpenImage} 
                    className="btn btn-secondary btn-sm"
                    title="Open image in new tab"
                  >
                    🔍 Open in New Tab
                  </button>
                </div>
                <div className="image-preview-content">
                  <img 
                    src={activeDocument.previewUrl} 
                    alt={activeDocument.file.name} 
                    className="preview-image-full"
                    onClick={handleOpenImage}
                  />
                </div>
                <div className="image-preview-footer">
                  <span className="image-filename">{activeDocument.file.name}</span>
                  <span className="image-filesize">{(activeDocument.file.size / 1024 / 1024).toFixed(2)} MB</span>
                </div>
              </div>
            )}

            {!activeDocument && documents.length === 0 && (
              <div className="placeholder-message">
                <h3>No image selected</h3>
                <p>Upload an image to see the preview here</p>
              </div>
            )}

            {!activeDocument && documents.length > 0 && (
              <div className="placeholder-message">
                <h3>No image selected</h3>
                <p>Select a document from the list to preview it here</p>
              </div>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="right-panel">
            {activeDocument?.step === ProcessingStep.PROCESSING && (
              <div className="status-message">
                <h3>Analyzing layout...</h3>
                <p>This may take a few moments.</p>
                <div className="loading-spinner"></div>
              </div>
            )}

            {activeDocument?.step === ProcessingStep.ERROR && (
              <div className="error-message">
                <h3>Error</h3>
                <p>{activeDocument.error}</p>
              </div>
            )}

            {activeDocument?.step === ProcessingStep.COMPLETED && activeDocument.results && (
              <div className="results-container">
                <DetectAbnormalLayoutResults 
                  results={activeDocument.results} 
                  onDownload={handleDownload}
                />
              </div>
            )}

            {activeDocument && !activeDocument.results && activeDocument.step === ProcessingStep.FILE_SELECTION && (
              <div className="placeholder-message">
                <h3>Ready to detect abnormal layout</h3>
                <p>Configure merchant name and reference images, then click "Detect Abnormal Layout" to begin.</p>
              </div>
            )}

            {!activeDocument && documents.length === 0 && (
              <div className="placeholder-message">
                <h3>Upload images to get started</h3>
                <p>
                  <strong>Configuration Scope:</strong><br/>
                  • LLM settings & auth are <strong>shared across all images</strong><br/>
                  • Merchant name & reference images are <strong>specific to each input image</strong>
                </p>
              </div>
            )}

            {!activeDocument && documents.length > 0 && (
              <div className="placeholder-message">
                <h3>Select a document</h3>
                <p>
                  Click on a document to configure its merchant name and reference images.
                  LLM settings and authentication are shared across all documents.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
